"use client";

import React, { useState, useEffect } from "react";
import { Search, Menu, X, House, CirclePlay, Send, User, ChevronDown, Newspaper, Shield, MapPin, Info, BookOpen, MessageSquare } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
const decodeLabel = (label: string) => label.replace(/&amp;/g, "&");
const mapMenuLabel = (label: string) => {
  const decoded = decodeLabel(label);
  if (decoded === "Redaksi") return "Redaksi";
  if (decoded === "Zona Pemuda Pancasila") return "Zona PP";
  if (decoded === "Kontak Kami") return "Kontak";
  return decoded;
};
function HeaderMenuConsumer({
  mobile,
  onClose
}: {
  mobile?: boolean;
  onClose?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [currentPath, setCurrentPath] = useState("");
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);
  if (loading) return <div className={mobile ? "space-y-4" : "flex gap-2"} />;
  const topLevelOrder = ["Beranda", "Berita", "Klarifikasi", "Kerjasama", "Opini", "Redaksi"];
  const processedItems = menuItems.filter(item => topLevelOrder.includes(mapMenuLabel(decodeLabel(item.label || ""))));
  const iconMap: Record<string, React.ReactNode> = {
    "Beranda": <House className="size-4" />,
    "Berita": <Newspaper className="size-4" />,
    "Klarifikasi": <Shield className="size-4" />,
    "Kerjasama": <Send className="size-4" />,
    "Opini": <MessageSquare className="size-4" />,
    "Redaksi": <BookOpen className="size-4" />
  };
  if (mobile) {
    return <div className="flex flex-col gap-2 mt-4">
        {processedItems.map((item, i) => {
        const label = mapMenuLabel(decodeLabel(item.label));
        const isActive = currentPath === item.href;
        return <Link key={item.id} to={item.href || "#"} onClick={onClose} className={`group flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all duration-300 [transform:translateZ(0)] text-white hover:underline hover:font-semibold hover:text-orange-300 ${isActive ? "font-semibold bg-orange-600/15 text-orange-400" : "font-medium"}`}>
              <span className="w-5 h-5 flex-shrink-0 bg-contain bg-no-repeat group-hover:opacity-80 transition-all duration-300">
                {iconMap[label]}
              </span>
              {label}
            </Link>;
      })}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="flex items-center gap-8 px-5 py-3 header-menu">
        {processedItems.map((item, i) => {
        const label = mapMenuLabel(decodeLabel(item.label));
        const isActive = currentPath === item.href;
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={`group flex items-center gap-2 rounded-md text-base font-medium tracking-wide text-white transition-all duration-300 [transform:translateZ(0)] hover:underline hover:font-semibold hover:text-orange-300 ${isActive ? "font-semibold bg-orange-600/15 text-orange-400" : ""}`}>
                  <span className="inline-block w-5 h-5 flex-shrink-0 bg-contain bg-no-repeat group-hover:opacity-80 transition-all duration-300">
                    {iconMap[label]}
                  </span>
                  {label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <>
      <style dangerouslySetInnerHTML={{
      __html: `
        .glossy-header {
          background: linear-gradient(to right, #7f1d1d, #ea580c, #7f1d1d);
          position: relative;
          overflow: hidden;
        }
        .glossy-header::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; height: 50%;
          background: linear-gradient(to bottom, rgba(255,255,255,0.3), transparent);
          pointer-events: none;
        }
        .glossy-nav-btn {
          background-color: #b91c1c;
          border: 1px solid #d97706;
        }
        .glossy-nav-btn:hover {
          background-color: #991b1b;
        }
      `
    }} />
      <header className="glossy-header fixed top-0 z-50 w-full h-16 md:h-[68px] lg:h-20 border-b-2 border-amber-500 flex items-center">
        <div className="mx-auto w-full max-w-[1280px] px-6 flex items-center justify-between gap-8 h-full">
          <div className="flex items-center gap-4">
            <Sheet>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[150px] bg-gradient-to-r from-red-900 via-orange-600 to-red-900" style={{
              backgroundColor: 'transparent'
            }}>
                <SheetHeader>
                  <SheetTitle className='text-white font-semibold'>Menu</SheetTitle>
                </SheetHeader>
                <MenuProvider menu_id="1046">
                  <HeaderMenuConsumer mobile={true} />
                </MenuProvider>
              </SheetContent>
            </Sheet>
            <Link to="/"><WvcLogo className="h-10 w-auto" /></Link>
          </div>
          <MenuProvider menu_id="1046">
            <HeaderMenuConsumer />
          </MenuProvider>
          <div className="flex items-center gap-3">
            <div className="relative hidden sm:block [transform:translateZ(0)]">
              <Input placeholder="Cari berita..." className="bg-white/90 rounded border border-black/20 px-3 py-2 h-10 w-40 lg:w-60 focus:border-blue-600 transition" />
              <Search className="absolute right-3 top-2.5 size-5 text-red-600" />
            </div>
            <Button asChild className="rounded bg-blue-600 text-white px-4 py-2 font-medium hover:bg-blue-800 transition [transform:translateZ(0)]">
              <Link to="/AccountPage"><User className="size-5" /></Link>
            </Button>
          </div>
        </div>
      </header>
      <div className="h-16 md:h-[68px] lg:h-20" />
    </>;
}